import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { confirmEmailVerification, type AccountState } from "../../api/accountsApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import AuthCardPage from "./AuthCardPage";

/** Opened from the link in a verification email: confirms the token once (Q-36). */
export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const token = query.get("token") ?? "";
  const { state, submit } = useSubmission();

  const [accountState, setAccountState] = useState<AccountState | null>(null);

  useEffect(() => {
    if (token !== "" && state.status === "idle") {
      submit(async () => {
        setAccountState(await confirmEmailVerification(token));
      });
    }
  }, [state.status, submit, token]);

  return (
    <AuthCardPage title={t("verify.title")}>
      {token === "" ? <StatusMessage tone="error">{t("verify.noToken")}</StatusMessage> : null}
      {token !== "" && (state.status === "idle" || state.status === "submitting") ? (
        <p className="app-text-muted mb-4">{t("verify.wait")}</p>
      ) : null}
      {state.status === "succeeded" ? (
        <StatusMessage tone="success">
          {accountState === "active" ? t("verify.successActive") : t("verify.successPendingApproval")}
        </StatusMessage>
      ) : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <AppButton variant="primary" className="mt-4" onClick={() => void navigate(appPaths.login())}>
        {t("verify.toApp")}
      </AppButton>
    </AuthCardPage>
  );
}
